import type { IngestRun } from "@plenum/shared/api";
import { useState } from "react";
import { ApiError, useApprovals, useDecideApproval, useIngest, useStats } from "../lib/api";
import { fmtInt, fmtMw } from "../lib/format";
import { Pill } from "../ui/Badge";
import { Track, TrackList } from "../ui/graph/Track";
import { useToast } from "../ui/Toast";

/** Ingest and enrichment run history, coverage KPIs and layer versions (PRD §12.1, §14.7). Admin only. */
export function AdminIngestPage() {
  const ingest = useIngest();
  const stats = useStats();
  const approvals = useApprovals();
  const decide = useDecideApproval();
  const toast = useToast();
  const [open, setOpen] = useState<number | null>(null);

  if (ingest.error) {
    return (
      <div className="p-6" style={{ color: "var(--danger)" }}>
        {ingest.error instanceof ApiError
          ? ingest.error.status === 403
            ? "Admin role required."
            : ingest.error.message
          : "Could not load"}
      </div>
    );
  }
  const s = stats.data;
  return (
    <div className="scroll-thin mx-auto h-full max-w-6xl overflow-auto p-6">
      <h1 className="text-[18px] font-semibold">Ingest and coverage</h1>
      <p className="text-[12px]" style={{ color: "var(--muted)" }}>
        Layer v{ingest.data?.layer.version ?? "—"} · OSM outlines v
        {ingest.data?.layer.osm_version ?? "—"} · agent runs {fmtInt(ingest.data?.agent_runs.total)}{" "}
        ({fmtInt(ingest.data?.agent_runs.last_24h)} in 24 h,{" "}
        {fmtInt(ingest.data?.agent_runs.failed_24h)} failed)
      </p>

      {s ? (
        <section
          className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-4 lg:grid-cols-6"
          data-testid="kpis"
        >
          <Kpi
            label="Sites"
            value={fmtInt(s.counts.sites)}
            sub={`${fmtInt(s.counts.sites_in_band)} with qualifying units`}
          />
          <Kpi
            label="Units"
            value={fmtInt(s.counts.units)}
            sub={`${fmtInt(s.counts.units_in_band)} in band · ${fmtInt(s.counts.units_in_core_band)} core`}
          />
          <Kpi label="Qualifying MW" value={fmtMw(s.totals.qualifying_mw)} />
          <Kpi
            label="Climate profiles"
            value={`${s.climate.profiles_pct} %`}
            sub="target 100 %"
            track={{ value: s.climate.profiles_pct, target: 100, max: 100 }}
          />
          <Kpi
            label="Cooling estimates"
            value={`${s.climate.estimates_pct} %`}
            sub="target 100 %"
            track={{ value: s.climate.estimates_pct, target: 100, max: 100 }}
          />
          <Kpi
            label="Est. MWh/yr"
            value={s.totals.est_mwh_yr === null ? "—" : fmtInt(s.totals.est_mwh_yr)}
          />
          <Kpi
            label="Top-500 with model"
            value={`${s.enrichment.top500_model_pct} %`}
            sub={`target ≥85 % · basis ${s.enrichment.top500_basis}`}
            track={{ value: s.enrichment.top500_model_pct, target: 85, max: 100 }}
          />
          <Kpi
            label="Top-500 cooling status"
            value={`${s.enrichment.top500_cooling_status_pct} %`}
            sub="target ≥60 %"
            track={{ value: s.enrichment.top500_cooling_status_pct, target: 60, max: 100 }}
          />
          <Kpi
            label="Top-500 with contact"
            value={`${s.enrichment.top500_contact_pct} %`}
            sub="target ≥70 %"
            track={{ value: s.enrichment.top500_contact_pct, target: 70, max: 100 }}
          />
          <Kpi
            label="Verified email"
            value={`${s.enrichment.top500_verified_email_pct} %`}
            sub="target ≥50 % (paid path)"
            track={{ value: s.enrichment.top500_verified_email_pct, target: 50, max: 100 }}
          />
          <Kpi
            label="Sites enriched"
            value={`${fmtInt(s.cost.sites_enriched)} / ${fmtInt(s.counts.sites_in_band)}`}
            sub={`${fmtInt(s.cost.failed_steps_open)} failed steps open · $${s.cost.agent_cost_usd.toFixed(0)} agent spend`}
            track={{
              value: s.cost.sites_enriched,
              target: s.counts.sites_in_band,
              max: Math.max(s.counts.sites_in_band, s.cost.sites_enriched),
            }}
          />
          <Kpi
            label="Cost month to date"
            value={`$${s.cost.month_to_date_usd.toFixed(2)}`}
            sub={`$${s.cost.enrichment_usd_per_site_avg.toFixed(2)} per site · target ≤1.50`}
            track={{
              value: s.cost.enrichment_usd_per_site_avg,
              target: 1.5,
              max: Math.max(3, s.cost.enrichment_usd_per_site_avg),
              ceiling: true,
            }}
          />
          <Kpi
            label="Claims"
            value={fmtInt(Object.values(s.counts.claims).reduce((a, b) => a + b, 0))}
            sub={
              Object.entries(s.counts.claims)
                .map(([k, v]) => `${v} ${k}`)
                .join(" · ") || "none"
            }
          />
        </section>
      ) : null}

      {s ? (
        <section className="mt-4 grid gap-4 md:grid-cols-3 text-[12px]">
          <div>
            <h2 className="eyebrow mb-1">Tiers</h2>
            <TrackList
              testId="tiers"
              ticks={18}
              rows={Object.entries(s.tiers).map(([t, n]) => ({
                label: t === "x" ? "unscored" : `Tier ${t}`,
                value: n,
                display: fmtInt(n),
              }))}
            />
          </div>
          <div>
            <h2 className="eyebrow mb-1">Frame class (in band)</h2>
            <TrackList
              ticks={18}
              tone="steel"
              rows={s.by_frame_class.map((f) => ({
                label: f.value,
                value: f.count,
                display: fmtInt(f.count),
              }))}
            />
          </div>
          <div>
            <h2 className="eyebrow mb-1">Top states (sites in band)</h2>
            <TrackList
              ticks={18}
              tone="steel"
              rows={s.by_state.slice(0, 10).map((f) => ({
                label: f.value,
                value: f.count,
                display: fmtInt(f.count),
              }))}
            />
          </div>
        </section>
      ) : null}

      <section className="mt-6">
        <h2 className="eyebrow mb-2">Approvals (PRD §0.2: paid spend, exports, Protected Paths)</h2>
        {approvals.data && approvals.data.items.length === 0 ? (
          <p className="text-[12px]" style={{ color: "var(--muted)" }}>
            No pending approvals.
          </p>
        ) : null}
        <ul className="flex flex-col gap-2" data-testid="approvals">
          {(approvals.data?.items ?? []).map((a) => (
            <li key={a.id} className="approval-card">
              <ApprovalSummary
                id={a.id}
                kind={a.kind}
                payload={a.payload}
                createdAt={a.created_at}
              />
              <div className="flex items-start gap-1">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={async () => {
                    try {
                      await decide.mutateAsync({ id: a.id, decision: "approve" });
                      toast(`Approved #${a.id}`);
                    } catch (e) {
                      toast(e instanceof ApiError ? e.message : "Failed", "danger");
                    }
                  }}
                >
                  Approve
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={async () => {
                    try {
                      await decide.mutateAsync({ id: a.id, decision: "reject" });
                      toast(`Rejected #${a.id}`);
                    } catch (e) {
                      toast(e instanceof ApiError ? e.message : "Failed", "danger");
                    }
                  }}
                >
                  Reject
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-6">
        <h2 className="eyebrow mb-2">Ingest runs</h2>
        <table className="tbl" data-testid="ingest-runs">
          <thead>
            <tr>
              <th className="r">#</th>
              <th>Step</th>
              <th>Version</th>
              <th>Status</th>
              <th className="r">Rows in</th>
              <th className="r">Upserted</th>
              <th>Started</th>
              <th>Finished</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(ingest.data?.runs ?? []).map((r: IngestRun) => (
              <RunRow
                key={r.id}
                r={r}
                open={open === r.id}
                onToggle={() => setOpen(open === r.id ? null : r.id)}
              />
            ))}
            {ingest.isLoading ? (
              <tr>
                <td colSpan={9} style={{ color: "var(--muted)" }}>
                  Loading…
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function RunRow({ r, open, onToggle }: { r: IngestRun; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr>
        <td className="num r">{r.id}</td>
        <td>{r.source}</td>
        <td className="num">{r.version_label ?? "—"}</td>
        <td>
          <Pill
            tone={
              r.status === "ok"
                ? "ok"
                : r.status === "running"
                  ? "gold"
                  : r.status === "failed"
                    ? "danger"
                    : "muted"
            }
          >
            {r.status}
          </Pill>
        </td>
        <td className="num r">{fmtInt(r.rows_in)}</td>
        <td className="num r">{fmtInt(r.rows_upserted)}</td>
        <td className="num">{r.started_at.replace("T", " ").slice(0, 19)}</td>
        <td className="num">
          {r.finished_at ? r.finished_at.replace("T", " ").slice(0, 19) : "—"}
        </td>
        <td>
          {r.report ? (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={onToggle}
              aria-expanded={open}
            >
              {open ? "hide" : "report"}
            </button>
          ) : null}
        </td>
      </tr>
      {open ? (
        <tr>
          <td colSpan={9}>
            <pre
              className="num max-h-64 overflow-auto whitespace-pre-wrap text-[11px]"
              style={{ color: "var(--muted)" }}
            >
              {JSON.stringify(r.report, null, 2)}
            </pre>
          </td>
        </tr>
      ) : null}
    </>
  );
}

/** Readable summary of an approval payload; the raw JSON stays one click away. */
function ApprovalSummary({
  id,
  kind,
  payload,
  createdAt,
}: {
  id: number;
  kind: string;
  payload: unknown;
  createdAt: string;
}) {
  const [raw, setRaw] = useState(false);
  const p = (payload ?? {}) as {
    reason?: string;
    site_ids?: number[];
    site_id?: number;
    agents?: string[];
    contact_mode?: string;
    allow_paid_apis?: boolean;
    budget_usd?: number;
  };
  const sites = p.site_ids?.length ?? (p.site_id ? 1 : 0);
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone="gold">{kind.replace(/_/g, " ")}</Pill>
        <span className="num">#{id}</span>
        <span className="font-medium">{p.reason ?? "approval requested"}</span>
      </div>
      <div className="approval-facts mt-1">
        {sites ? (
          <span>
            sites <b className="num">{fmtInt(sites)}</b>
          </span>
        ) : null}
        {p.agents?.length ? (
          <span>
            agents <b>{p.agents.join(", ")}</b>
          </span>
        ) : null}
        {p.contact_mode ? (
          <span>
            mode <b>{p.contact_mode}</b>
          </span>
        ) : null}
        {p.allow_paid_apis ? (
          <span>
            <b>paid contact APIs</b>
          </span>
        ) : null}
        {p.budget_usd ? (
          <span>
            budget <b className="num">${p.budget_usd}</b>
          </span>
        ) : null}
        <span>
          requested <b className="num">{createdAt.replace("T", " ").slice(0, 16)}</b>
        </span>
        <button type="button" className="cite-toggle" onClick={() => setRaw((r) => !r)}>
          {raw ? "hide payload" : "payload"}
        </button>
      </div>
      {raw ? (
        <pre
          className="num mt-1 max-h-40 overflow-auto whitespace-pre-wrap text-[11px]"
          style={{ color: "var(--muted)" }}
        >
          {JSON.stringify(payload, null, 2)}
        </pre>
      ) : null}
    </div>
  );
}

function Kpi({
  label,
  value,
  sub,
  track,
}: {
  label: string;
  value: string;
  sub?: string;
  track?: { value: number; target?: number; max?: number; ceiling?: boolean };
}) {
  return (
    <div
      className="rounded border px-2 py-1.5"
      style={{ borderColor: "var(--line)", background: "var(--panel-2)" }}
    >
      <div className="eyebrow">{label}</div>
      <div className="num text-[15px]">{value}</div>
      {track ? (
        <div className="mt-0.5">
          <Track
            value={track.value}
            target={track.target}
            max={track.max}
            ceiling={track.ceiling}
            ticks={20}
            label={`${label} ${value}${sub ? `, ${sub}` : ""}`}
          />
        </div>
      ) : null}
      {sub ? (
        <div className="text-[11px]" style={{ color: "var(--muted-2)" }}>
          {sub}
        </div>
      ) : null}
    </div>
  );
}
